import { FC, useRef } from 'react';
import { useState, useCallback } from 'react';
import { EditableDataTable, EditableColumn } from '@/components/ui';
import { CompanySource, LastScrapedPost } from '../types';
import { PostMetricsFooter } from './PostMetricsFooter';
import { HeaderWithTooltip } from './HeaderWithTooltip';
import { ShareBar } from './ShareBar';
import { FrequencyDisplay } from './FrequencyDisplay';
import { ArticleDisplay } from './ArticleDisplay';
import { ScoreDisplay } from './ScoreDisplay';
import api from '@/utils/api';

/**
 * Detail Panel Content Component
 * Handles fetching and displaying article metrics for expanded rows
 */
const DetailPanelContent: FC<{
  row: CompanySource;
  topSources: CompanySource[];
  loadingArticles: Map<string, boolean>;
  articleData: Map<string, LastScrapedPost[] | null>;
}> = ({ row, topSources, loadingArticles, articleData }) => {
  const rowIndex = topSources.findIndex(s => s.company === row.company);
  const rowKey = rowIndex >= 0 ? `source-${rowIndex}` : `source-${row.company}`;
  const isLoading = loadingArticles.get(rowKey);
  const articles = articleData.get(rowKey);

  // Don't fetch here - let handleRowExpand handle it to avoid duplicate calls
  // The DetailPanelContent will just display the data that's already fetched

  if (isLoading) {
    return (
      <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-200">
        <div className="flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs text-gray-500">
            Loading metrics...
          </p>
        </div>
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-200">
        <p className="text-xs text-gray-400 text-center">
          No metrics available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {articles.map((article, index) => (
        <PostMetricsFooter
          key={index}
          post={article}
          companyName={row.company}
          isFirst={index === 0}
          isLast={index === articles.length - 1}
          totalCount={articles.length}
        />
      ))}
    </div>
  );
};

interface CompetitorTabProps {
  description: string;
  topSources: CompanySource[];
  loading?: boolean;
  error?: string | null;
  onRefetch?: (sortBy?: string, sortOrder?: 'asc' | 'desc', search?: string) => Promise<void>;
  onGlobalSearch?: (query: string) => Promise<void>;
  globalFilterValue?: string;
}

/**
 * Competitor Analysis Tab Component
 * Shows competitor activity and top content sources
 */
export const CompetitorTab: FC<CompetitorTabProps> = ({
  description,
  topSources,
  loading = false,
  error = null,
  onRefetch,
  onGlobalSearch,
  globalFilterValue,
}) => {
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [loadingArticles, setLoadingArticles] = useState<Map<string, boolean>>(new Map());
  const [articleData, setArticleData] = useState<Map<string, LastScrapedPost[] | null>>(new Map());

  const mapColumnKeyToBackendField = useCallback((columnKey: string): string => {
    const mapping: Record<string, string> = {
      'company': 'company',
      'articles': 'articles',
      'avgScore': 'avgScore',
      'avgPosts': 'avgPosts',
      'share': 'share',
    };
    return mapping[columnKey] || columnKey;
  }, []);

  const handleSortingChange = useCallback((newSorting: Array<{ id: string; desc: boolean }>) => {
    setSorting(newSorting);
    if (onRefetch && newSorting.length > 0) {
      const sortItem = newSorting[0];
      const sortBy = mapColumnKeyToBackendField(sortItem.id);
      const sortOrder = sortItem.desc ? 'desc' : 'asc';
      onRefetch(sortBy, sortOrder);
    } else if (onRefetch) {
      // Reset to default sorting
      onRefetch('articles', 'desc');
    }
  }, [onRefetch, mapColumnKeyToBackendField]);

  // Track ongoing fetches to prevent duplicate calls
  const fetchingRef = useRef<Set<string>>(new Set());

  const fetchLatestArticle = useCallback(async (carionCompanyId: number, rowKey: string) => {
    // Check if we already have the data
    if (articleData.has(rowKey)) {
      return;
    }

    // Check if a fetch is already in progress for this row
    if (fetchingRef.current.has(rowKey)) {
      return;
    }

    // Mark as fetching
    fetchingRef.current.add(rowKey);
    setLoadingArticles(prev => new Map(prev).set(rowKey, true));

    try {
      const { data } = await api.get<LastScrapedPost[]>(
        `/idm/competitors/${carionCompanyId}/latest-article`
      );
      setArticleData(prev => new Map(prev).set(rowKey, (data && data.length > 0) ? data : null));
    } catch (err: unknown) {
      console.error('Failed to fetch latest articles:', err);
      setArticleData(prev => new Map(prev).set(rowKey, null));
    } finally {
      fetchingRef.current.delete(rowKey);
      setLoadingArticles(prev => {
        const newMap = new Map(prev);
        newMap.delete(rowKey);
        return newMap;
      });
    }
  }, [articleData]);

  const handleRowExpand = useCallback((row: CompanySource, rowKey: string, isExpanded: boolean) => {
    if (isExpanded && row.id) {
      // Only fetch if we don't have the data and aren't already fetching
      if (!articleData.has(rowKey) && !fetchingRef.current.has(rowKey)) {
        fetchLatestArticle(row.id, rowKey);
      }
    }
  }, [fetchLatestArticle, articleData]);

  const columns: EditableColumn<CompanySource>[] = [
    {
      key: 'company',
      header: (
        <HeaderWithTooltip
          title="Company"
          tooltip="Companies in your industry. Your selected competitors are highlighted with a star."
        />
      ),
      accessor: 'company',
      enableSorting: true,
      render: (row) => (
        <div className="flex items-center gap-2.5 group">
          <div className="flex items-center gap-2 relative">
            {row.isSelected && (
              <>
                <div
                  className="absolute -left-3 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 rounded-full cursor-help"
                  title="Your selected competitor"
                />
                <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-gradient-to-b from-blue-400 via-indigo-400 to-purple-400 rounded-full blur-sm opacity-60 pointer-events-none" />
              </>
            )}
            <span className={row.isSelected ? 'font-semibold text-gray-900' : 'text-gray-700'}>
              {row.company}
            </span>
            {row.isSelected && (
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-[10px] font-medium text-indigo-600 uppercase tracking-wider">
                Tracked
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'articles',
      header: (
        <HeaderWithTooltip
          title="Articles"
          subtitle="posts analyzed"
          tooltip="Total number of LinkedIn posts extracted and analyzed from this competitor."
        />
      ),
      accessor: (row) => <ArticleDisplay value={row.articles} />,
      enableSorting: true,
    },
    {
      key: 'avgScore',
      header: (
        <HeaderWithTooltip
          title="Average Score"
          subtitle="post performance"
          tooltip="A calculation of how well a post performed by combining likes, comments, and shares. A higher score means the content was more successful at engaging the audience."
        />
      ),
      accessor: (row) => <ScoreDisplay value={row.avgScore} />,
      enableSorting: true,
    },
    {
      key: 'avgPosts',
      header: (
        <HeaderWithTooltip
          title="Average Post (Weekly)"
          subtitle="frequency"
          tooltip="Average number of posts published per week. Shows how active this competitor is on LinkedIn."
        />
      ),
      accessor: (row) => <FrequencyDisplay value={row.avgPosts} />,
      enableSorting: true,
    },
    {
      key: 'share',
      header: (
        <HeaderWithTooltip
          title="Share of Total"
          subtitle="% of posts"
          tooltip="Percentage of total content this competitor represents among all monitored companies. Higher share means more dominant content presence."
        />
      ),
      accessor: (row) => <ShareBar value={row.share} />,
      enableSorting: true,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Competitor Analysis
          </h2>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
        <div className="mt-6">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Competitor Analysis
          </h2>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
        <div className="mt-6 text-red-600">{error}</div>
      </div>
    );
  }

  // Calculate the latest scraped date from all competitors
  const getLatestScrapedDate = () => {
    const dates = topSources
      .map((source) => source.lastScrapedAt)
      .filter((date): date is string => !!date)
      .map((date) => new Date(date))
      .filter((date) => !isNaN(date.getTime()));

    if (dates.length === 0) return null;

    const latest = new Date(Math.max(...dates.map((d) => d.getTime())));
    return latest;
  };

  const latestScrapedDate = getLatestScrapedDate();
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Competitor Analysis
        </h2>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>

      <div className="mt-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">
            Top Sources
          </h3>
          {latestScrapedDate && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Recent Metrics Update:</span>{' '}
              <span>{formatDate(latestScrapedDate)}</span>
            </div>
          )}
        </div>
        {topSources.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            No competitor data available.
          </div>
        ) : (
          <EditableDataTable
            columns={columns}
            data={topSources}
            keyExtractor={(_, idx) => `source-${idx}`}
            enableEditing={false}
            initialEditingMode="cell"
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting={!!onRefetch}
            onGlobalSearch={onGlobalSearch}
            globalFilterValue={globalFilterValue}
            renderDetailPanel={(row) => (
              <DetailPanelContent
                row={row}
                topSources={topSources}
                loadingArticles={loadingArticles}
                articleData={articleData}
              />
            )}
            onExpandedChange={(row, isExpanded) => {
              const rowIndex = topSources.findIndex(s => s.company === row.company);
              const rowKey = rowIndex >= 0 ? `source-${rowIndex}` : `source-${row.company}`;
              handleRowExpand(row, rowKey, isExpanded);
            }}
          />
        )}
      </div>
    </div>
  );
};
